/**
 * Ring Sizing Add-on Logic
 * Automatically adds sizing kit when customer doesn't know their ring size
 * Handles AJAX-loaded variant selectors
 */

// ============================================================================
// GLOBAL: Cart Quantity Sync (runs on all pages)
// ============================================================================
(function() {
  // Use window-level variables to prevent duplicate instances from interfering
  if (!window.__ringSizingSync) {
    window.__ringSizingSync = {
      activeSyncs: new Set(),
      processingStartTime: 0,
      activeFetches: new Set()
    };
  }
  
  const syncState = window.__ringSizingSync;
  const activeSyncs = syncState.activeSyncs;
  const activeFetches = syncState.activeFetches;

  async function normalizeSizingKitQuantities(syncId) {
    // Add loading state to cart
    const cartDrawer = document.querySelector('cart-drawer');
    const checkoutButton = document.querySelector('[name="checkout"], .cart-drawer__footer button[type="submit"]');
    
    if (cartDrawer) {
      cartDrawer.classList.add('cart--syncing');
    }
    
    if (checkoutButton) {
      checkoutButton.setAttribute('disabled', 'true');
      checkoutButton.classList.add('btn--loading');
    }

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`);
      const cart = await response.json();

      const sizingKitItems = cart.items.filter((item) => item.properties?._addon_type === 'Sizing Kit');

      for (const [index, item] of sizingKitItems.entries()) {
        const targetQuantity = index === 0 ? 1 : 0;

        if (item.quantity === targetQuantity) {
          continue;
        }

        const fetchKey = `${item.key}:${targetQuantity}`;
        if (activeFetches.has(fetchKey)) {
          continue;
        }

        activeFetches.add(fetchKey);

        try {
          await fetch(`${window.Shopify.routes.root}cart/change.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              id: item.key,
              quantity: targetQuantity
            })
          });
        } finally {
          activeFetches.delete(fetchKey);
        }
      }
      
    } catch (error) {
      // Silent error handling
    } finally {
      // Remove loading state
      const cartDrawer = document.querySelector('cart-drawer');
      const checkoutButton = document.querySelector('[name="checkout"], .cart-drawer__footer button[type="submit"]');
      
      if (cartDrawer) {
        cartDrawer.classList.remove('cart--syncing');
      }
      
      if (checkoutButton) {
        checkoutButton.removeAttribute('disabled');
        checkoutButton.classList.remove('btn--loading');
      }
      
      // Trigger cart refresh after everything is cleaned up
      document.dispatchEvent(new CustomEvent('cart:refresh', {
        detail: { open: false },
        bubbles: true
      }));
      
      // Remove this sync from active set immediately
      activeSyncs.delete(syncId);
    }
  }

  // Async handler for cart updates
  async function handleCartUpdate(eventId, data) {
    try {
      // Skip if this update came from our own sync
      if (data?.source === 'qty-sync') {
        return;
      }
      
      if (!data || !data.cart) {
        return;
      }
      
      const cart = data.cart;

      const sizingKitItems = cart.items.filter((item) => item.properties?._addon_type === 'Sizing Kit');
      const needsNormalization = sizingKitItems.some((item, index) => {
        const targetQuantity = index === 0 ? 1 : 0;
        return item.quantity !== targetQuantity;
      });

      if (needsNormalization) {
        const syncId = 'sizing-kit-normalization';

        if (activeSyncs.has(syncId)) {
          return;
        }

        activeSyncs.add(syncId);

        try {
          await normalizeSizingKitQuantities(syncId);
        } finally {
          activeSyncs.delete(syncId);
        }
      }
      
    } catch (error) {
      // Silent error handling
    }
  }

  // Initialize when FoxTheme is ready
  function initQuantitySync() {
    if (!window.FoxTheme || !FoxTheme.pubsub) {
      setTimeout(initQuantitySync, 100);
      return;
    }
    
    // Listen to FoxTheme cart update events  
    FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (data) => {
      const eventId = Math.random().toString(36).substr(2, 9);
      const now = Date.now();
      
      // STRICT CHECK: Block if processing started within last 500ms OR if any syncs are active
      const timeSinceLastStart = syncState.processingStartTime > 0 ? (now - syncState.processingStartTime) : -1;
      if (syncState.processingStartTime > 0 && timeSinceLastStart < 500) {
        return;
      }
      
      if (activeSyncs.size > 0) {
        return;
      }
      
      // Set processing start time IMMEDIATELY (synchronously, before any async work)
      syncState.processingStartTime = now;
      
      // Call async handler - it will release the lock when done
      handleCartUpdate(eventId, data).finally(() => {
        // Release lock immediately after sync completes
        syncState.processingStartTime = 0;
      });
    });
  }

  // Start initialization
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initQuantitySync);
  } else {
    initQuantitySync();
  }
})();

// ============================================================================
// Product Page Component
// ============================================================================
if (!customElements.get('ring-sizing-addon')) {
  customElements.define(
    'ring-sizing-addon',
    class RingSizingAddon extends HTMLElement {
      constructor() {
        super();
        this.sizingKitVariantId = this.dataset.sizingKitVariant;
        this.sizingKitMessage = this.dataset.message;
        this.sizingKitYesMessage = this.dataset.messageYes;
        this.variants = this.parseVariants();
        this.productForm = null;
        this.productInfo = null;
        this.isProcessing = false;
      }

      connectedCallback() {
        if (!this.sizingKitVariantId) return;

        this.productInfo = this.closest('product-info');
        if (!this.productInfo) return;

        this.productForm = this.productInfo.querySelector('form[is="product-form"]');
        if (!this.productForm) return;

        // Set up event delegation (works with AJAX-loaded content)
        this.setupEventDelegation();
        
        // Listen to form submission
        this.productForm.addEventListener('submit', this.handleFormSubmit.bind(this), true);

        // Watch for AJAX changes to variant-selects
        this.setupMutationObserver();

        // Check initial state
        setTimeout(() => this.handleOptionChange(), 200);
      }

      setupMutationObserver() {
        // Watch for changes to variant-selects content (AJAX updates)
        const variantSelects = this.productInfo.querySelector('variant-selects');
        if (!variantSelects) return;

        let mutationTimeout;
        this.observer = new MutationObserver(() => {
          // Debounce - wait for DOM to fully settle after AJAX
          clearTimeout(mutationTimeout);
          mutationTimeout = setTimeout(() => {
            // Re-apply hide/show logic AFTER DOM has changed
            this.handleOptionChange();
          }, 300); // Wait 300ms for DOM to settle
        });

        // Observe changes to variant-selects
        this.observer.observe(variantSelects, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class']
        });
      }


      setupEventDelegation() {
        // Use event delegation on productInfo (parent that doesn't change)
        // This works even when variant-selects content is replaced via AJAX
        this.productInfo.addEventListener('change', (event) => {
          // Check if the change is from a radio input in variant-selects
          if (event.target.matches('variant-selects input[type="radio"]')) {
            const variantSelects = this.productInfo.querySelector('variant-selects');
            if (!variantSelects) return;

            // Get all fieldsets
            const allFieldsets = variantSelects.querySelectorAll('fieldset');
            if (allFieldsets.length < 2) return;

            // Check if this is option 2 (second fieldset)
            const option2Fieldset = allFieldsets[1];
            if (option2Fieldset.contains(event.target)) {
              // This is option 2 change - handle it
              setTimeout(() => this.handleOptionChange(), 50);
            }
          }
        });
      }

      disconnectedCallback() {
        // Cleanup mutation observer
        if (this.observer) {
          this.observer.disconnect();
        }
      }

      parseVariants() {
        try {
          return JSON.parse(this.dataset.variants || '[]');
        } catch (error) {
          return [];
        }
      }

      normalizeValue(value) {
        return (value || '')
          .toString()
          .trim()
          .toLowerCase()
          .replace(/\s+/g, ' ');
      }

      isSizeLikeValue(value) {
        const normalized = this.normalizeValue(value);
        if (!normalized) return false;

        // Ring sizes are typically numeric or numeric-prefixed values such as "7", "54", or "US 7".
        return /\d/.test(normalized);
      }

      findUnknownSizeValue(container) {
        if (!container) return null;

        const radios = Array.from(container.querySelectorAll('input[type="radio"]'));
        const nonSizeRadio = radios.find((radio) => !this.isSizeLikeValue(radio.value));
        return nonSizeRadio ? nonSizeRadio.value : null;
      }

      getAddonTriggerValues(unknownSizeValue) {
        if (!unknownSizeValue || !Array.isArray(this.variants)) return [];

        const normalizedUnknownSizeValue = this.normalizeValue(unknownSizeValue);
        const triggerValues = new Set();

        this.variants.forEach((variant) => {
          if (this.normalizeValue(variant.option3) === normalizedUnknownSizeValue) {
            triggerValues.add(variant.option2);
          }
        });

        return Array.from(triggerValues);
      }

      isAddonActive(option2Value, unknownSizeValue) {
        const triggerValues = this.getAddonTriggerValues(unknownSizeValue);
        if (!triggerValues.length) return false;

        const normalizedOption2Value = this.normalizeValue(option2Value);
        return triggerValues.some((value) => this.normalizeValue(value) === normalizedOption2Value);
      }

      handleOptionChange() {
        if (this.isProcessing) return;
        this.isProcessing = true;

        // Wait for any ongoing AJAX to complete
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const variantSelects = this.productInfo.querySelector('variant-selects');
            if (!variantSelects) {
              this.isProcessing = false;
              return;
            }

            const option2Value = this.getOption2Value(variantSelects);
            this.currentKnowsSizeValue = option2Value;
            const option3Container = this.getOption3Container(variantSelects);

            if (!option3Container) {
              this.isProcessing = false;
              return;
            }

            const unknownSizeValue = this.findUnknownSizeValue(option3Container);
            const addonActive = this.isAddonActive(option2Value, unknownSizeValue);

            if (addonActive) {
              this.hideRealSizes(option3Container, unknownSizeValue);
              this.autoSelectUnknownSize(option3Container, unknownSizeValue);
              this.showSizingKitMessage();
            } else {
              this.showRealSizes(option3Container, unknownSizeValue);
              this.showSizingKitMessage();
            }

            setTimeout(() => {
              this.isProcessing = false;
            }, 150);
          });
        });
      }

      getOption2Value(variantSelects) {
        const allFieldsets = variantSelects.querySelectorAll('fieldset');
        if (allFieldsets.length >= 2) {
          const option2Fieldset = allFieldsets[1];
          const checkedRadio = option2Fieldset.querySelector('input[type="radio"]:checked');
          if (checkedRadio) {
            return checkedRadio.value;
          }
        }
        return null;
      }

      getOption3Container(variantSelects) {
        const allFieldsets = variantSelects.querySelectorAll('fieldset');
        if (allFieldsets.length >= 3) {
          return allFieldsets[2];
        }
        return null;
      }

      hideRealSizes(container, unknownSizeValue) {
        const radios = container.querySelectorAll('input[type="radio"]');
        
        radios.forEach(radio => {
          const label = container.querySelector(`label[for="${radio.id}"]`);
          const isUnknownSize = this.normalizeValue(radio.value) === this.normalizeValue(unknownSizeValue);
          
          if (!isUnknownSize) {
            radio.style.display = 'none';
            radio.disabled = true;
            if (label) label.style.display = 'none';
          } else {
            radio.style.display = '';
            radio.disabled = false;
            if (label) label.style.display = '';
          }
        });
      }

      showRealSizes(container, unknownSizeValue) {
        const radios = container.querySelectorAll('input[type="radio"]');
        let firstAvailableRadio = null;
        
        radios.forEach(radio => {
          const label = container.querySelector(`label[for="${radio.id}"]`);
          const isUnknownSize = this.normalizeValue(radio.value) === this.normalizeValue(unknownSizeValue);
          
          if (isUnknownSize) {
            radio.style.display = 'none';
            radio.disabled = true;
            if (label) label.style.display = 'none';
          } else {
            radio.style.display = '';
            radio.disabled = false;
            if (label) label.style.display = '';
            
            if (!firstAvailableRadio && !radio.classList.contains('disabled')) {
              firstAvailableRadio = radio;
            }
          }
        });
        
        // Auto-select first available size
        if (firstAvailableRadio && !firstAvailableRadio.checked) {
          firstAvailableRadio.checked = true;
          // Trigger change to update the hidden variant ID input
          firstAvailableRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      autoSelectUnknownSize(container, unknownSizeValue) {
        const radioButton = Array.from(container.querySelectorAll('input[type="radio"]')).find((radio) => {
          return this.normalizeValue(radio.value) === this.normalizeValue(unknownSizeValue);
        });

        if (radioButton && !radioButton.disabled && !radioButton.checked) {
          radioButton.checked = true;
          // Trigger change to update the hidden variant ID input
          radioButton.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      getCurrentSizingMessage() {
        if (this.currentKnowsSizeValue) {
          const value = this.currentKnowsSizeValue.toLowerCase();

        if (value.includes('yes') || value.includes('ja')) {
             return this.dataset.messageYes || this.sizingKitMessage;
           }
       }

  return this.sizingKitMessage;
}


      showSizingKitMessage() {
        let message = this.querySelector('.sizing-kit-message');
        if (!message) {
          message = document.createElement('div');
          message.className = 'sizing-kit-message alert alert--info blocks-radius-md';
          message.innerHTML = `
            <svg class="icon icon-info" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
              <path d="M8 7V11M8 5V5.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            <span>${this.getCurrentSizingMessage()}</span>
          `;
          this.appendChild(message);
        }
        else {
           const span = message.querySelector('span');
             if (span) {
                span.textContent = this.getCurrentSizingMessage();
        }
         }
               message.style.display = 'flex';
      }

      hideSizingKitMessage() { 
        const message = this.querySelector('.sizing-kit-message');
        if (message) {
          message.style.display = 'none';
        }
      }

      handleFormSubmit(event) {
        const variantSelects = this.productInfo.querySelector('variant-selects');
        if (!variantSelects) return;

        const option2Value = this.getOption2Value(variantSelects);
        this.currentKnowsSizeValue = option2Value;
        const option3Container = this.getOption3Container(variantSelects);
        const unknownSizeValue = this.findUnknownSizeValue(option3Container);
        
        // Only add sizing kit when the add-on trigger value is selected
        if (!this.isAddonActive(option2Value, unknownSizeValue)) {
          return;
        }

        // Prevent default form submission
        event.preventDefault();
        event.stopPropagation();

        // Add both products to cart
        this.addBothProductsToCart();
      }

      async addBothProductsToCart() {
        const submitButton = this.productForm.querySelector('[type="submit"]');
        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.classList.add('btn--loading');

        try {
          // Get current form data
          const formData = new FormData(this.productForm);
          const mainVariantId = formData.get('id');
          const quantity = parseInt(formData.get('quantity') || '1');
          
          // Get product info for properties
          const productTitle = this.productInfo.querySelector('.product__title')?.textContent?.trim() || 'Ring';

          // STEP 1: Add parent item first (standard add to cart)
          const parentFormData = new FormData();
          parentFormData.append('id', mainVariantId);
          parentFormData.append('quantity', quantity);

          const parentResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            body: parentFormData,
            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            }
          });
          
          const parentResult = await parentResponse.json();

          if (parentResult.status) {
            this.displayError(parentResult.description || 'Der opstod en fejl. Prøv venligst igen.');
            submitButton.removeAttribute('aria-disabled');
            submitButton.classList.remove('btn--loading');
            return;
          }

          // Get the parent item's key from response
          const parentItemKey = parentResult.key;

          // STEP 2: Add child item only if there isn't already a sizing kit in cart
          let cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
          let cart = await cartResponse.json();
          const hasSizingKitInCart = cart.items.some((item) => item.properties?._addon_type === 'Sizing Kit');

          if (!hasSizingKitInCart) {
            const childParams = new URLSearchParams();
            childParams.append('id', this.sizingKitVariantId);
            childParams.append('quantity', '1');
            childParams.append('parent_line_key', parentItemKey); // Native Shopify parent-child link
            childParams.append('properties[_main_product_name]', productTitle);
            childParams.append('properties[For]', productTitle);
            childParams.append('properties[_addon_type]', 'Sizing Kit');
            childParams.append('properties[_free_gift]', 'Gratis – find din størrelse');

            await fetch(`${window.Shopify.routes.root}cart/add.js`, {
              method: 'POST',
              body: childParams,
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'X-Requested-With': 'XMLHttpRequest'
              }
            });
          }

          // STEP 3: Fetch complete updated cart
          cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
          cart = await cartResponse.json();
          
          // STEP 4: Trigger ALL cart update events
          this.triggerCartUpdate(cart, parentResult);

          // STEP 5: Show cart drawer
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            cartDrawer.show(submitButton);
          }

        } catch (error) {
          this.displayError('Der opstod en fejl. Prøv venligst igen.');
        } finally {
          submitButton.removeAttribute('aria-disabled');
          submitButton.classList.remove('btn--loading');
        }
      }

      triggerCartUpdate(cart, addedItems) {
        // Trigger all necessary cart update events for the theme
        
        // 1. FoxTheme pub/sub cart update (CRITICAL for Hyper theme)
        if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS) {
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { 
            cart: cart,
            source: 'ring-sizing-addon'
          });
        }
        
        // 2. Standard Shopify cart update event
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart: cart },
          bubbles: true
        }));
        
        // 3. Product added event
        document.dispatchEvent(new CustomEvent('product-ajax:added', {
          detail: { 
            product: addedItems,
            cart: cart
          },
          bubbles: true
        }));

        // 4. Trigger cart refresh event
        document.dispatchEvent(new CustomEvent('cart:refresh', {
          detail: { cart: cart },
          bubbles: true
        }));

        // 5. Update cart count everywhere
        const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count, cart-count, [class*="cart-count"]');
        cartCountElements.forEach(el => {
          if (cart.item_count < 100) {
            el.textContent = cart.item_count;
          } else {
            el.textContent = '99+';
          }
          // Remove hidden attribute if present
          el.removeAttribute('hidden');
        });

        // 6. Force cart drawer to refresh its content
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.refresh === 'function') {
          cartDrawer.refresh();
        }

        // 7. Refresh cart items display
        const cartItems = document.querySelector('cart-items');
        if (cartItems && typeof cartItems.refresh === 'function') {
          cartItems.refresh();
        }
      }

      displayError(message) {
        // Show error in the product form error container
        const errorContainer = this.productForm.querySelector('.product-form__error-message');
        if (errorContainer) {
          errorContainer.textContent = message;
          errorContainer.removeAttribute('hidden');
          
          // Auto-hide after 5 seconds
          setTimeout(() => {
            errorContainer.setAttribute('hidden', '');
          }, 5000);
        } else {
          // Fallback to alert
          alert(message);
        }
      }
    }
  );
}
